// Dominio del modo "Ambiente completo" — 2 a 4 productos de la marca en UNA sola foto de
// ambiente, sólo para marcas del rubro Hogar y Decoración
// (ADR-2026-09-03-modo-ambiente-completo).
//
// Vive aparte de la pantalla (`modes/AmbienteCompletoMode.tsx`) por lo mismo que
// `mostrarComoFunciona.ts` y `vistasMuebles.ts`: son las listas y las reglas que decide
// producto —cuántos productos entran, qué roles hay, cuánto cuesta una escena— y
// cambiarlas no debería obligar a abrir el componente. Cuando exista backend, los `id`
// de acá son los que van a viajar en el payload.

import { creditosPorResolucion, type ResolucionImagen } from '../../lib/resolucionImagen';
import {
  AMBIENTES,
  AMBIENTE_PROPIO,
  AMBIENTE_PROPIO_ID,
  ambientePorId,
  ratioDeFormato,
  type Ambiente,
  type AmbienteElegido,
  type AmbienteId,
} from './mostrarComoFunciona';

/**
 * El ambiente —living, comedor, dormitorio, balcón, estudio— es EL MISMO catálogo que
 * usa "Mostrar cómo funciona", y se importa en vez de copiarse: son los dos modos del
 * rubro que preguntan "dónde pasa esto", y con dos listas paralelas la primera foto que
 * se agregue va a estar en una sola. Se re-exporta desde acá para que la pantalla tenga
 * un único import de dominio.
 *
 * Si mañana entra un tercer modo con la misma pregunta, la lista se muda a un archivo
 * propio del rubro y los dos modos importan de ahí.
 *
 * Con la lista viaja también la opción de ESCRIBIRLO (`AMBIENTE_PROPIO`): los cinco
 * curados no cierran nunca —un local, un consultorio, una terraza con pérgola— y el
 * permiso para describirlo es parte de la misma decisión, no un catálogo aparte
 * (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).
 */
export {
  AMBIENTES,
  AMBIENTE_PROPIO,
  AMBIENTE_PROPIO_ID,
  ambientePorId,
  ratioDeFormato,
};
export type { Ambiente, AmbienteElegido, AmbienteId };

/**
 * La séptima opción del ambiente, PROPIA de este modo: el usuario sube una FOTO del lugar
 * y los muebles se arman ahí (ADR-2026-09-28-ambiente-completo-fondo-propio).
 *
 * Es la misma subida de fondos de Modelo —mismo bucket, misma tabla
 * `fondos_personalizados`, misma "Mis fondos"—, así que lo que se sube desde acá aparece
 * en Modelo y al revés. NO vive en `mostrarComoFunciona.ts` con las otras dos porque un
 * video no arranca de una foto fija de fondo; es una decisión de la foto de ambiente.
 *
 * Igual que `AMBIENTE_PROPIO`, vive aparte de `AMBIENTES`: no es una escena curada sino un
 * permiso, y qué foto es se guarda al lado del id (`FondoGuardado`), como el texto del
 * ambiente descrito.
 */
export const AMBIENTE_FONDO_ID = 'fondo' as const;

export const AMBIENTE_FONDO = {
  id: AMBIENTE_FONDO_ID,
  nombre: 'Subí tu fondo',
  descripcion: 'Una foto del lugar, tuya. Los muebles se arman ahí.',
} as const;

/** Lo que puede quedar elegido como ambiente en este modo: curado, descrito o una foto. */
export type AmbienteDeEscena = AmbienteElegido | typeof AMBIENTE_FONDO_ID;

/** El fondo propio elegido: lo que el pedido necesita saber de un `FondoGuardado`. */
export interface FondoDeEscena {
  /** Nombre del archivo en el bucket `fondos_personalizados` (`<company>/full/<fileName>`). */
  fileName: string;
  nombre: string;
}

/**
 * Cómo viaja el ambiente en el pedido al backend.
 *
 * Tres formas, una por opción: el id cuando es un curado; `{ tipo: 'propio', texto }`
 * cuando lo escribió el usuario; y `{ tipo: 'fondo', file_name, nombre }` cuando subió
 * una foto — `file_name` con el mismo nombre que usa el resto de la app para un fondo
 * guardado (`background_file_name` en `fondoDelPayload.ts`), que es lo que el backend
 * resuelve contra el bucket.
 */
export type AmbienteDelPedido =
  | string
  | { tipo: 'propio'; texto: string }
  | { tipo: 'fondo'; file_name: string; nombre: string };

export const ambienteDelPedido = (
  ambienteId: AmbienteDeEscena,
  ambientePropio: string,
  fondo: FondoDeEscena | null,
): AmbienteDelPedido => {
  if (ambienteId === AMBIENTE_PROPIO_ID) {
    return { tipo: 'propio', texto: ambientePropio.trim() };
  }
  if (ambienteId === AMBIENTE_FONDO_ID) {
    return { tipo: 'fondo', file_name: fondo?.fileName ?? '', nombre: fondo?.nombre ?? '' };
  }
  return ambienteId;
};

/**
 * Qué le falta a la decisión del ambiente para poder generar, o `null` si está completa.
 *
 * Un ambiente propio sin escribir y un fondo propio sin foto son una escena sin lugar: el
 * Generar espera. Va acá y no en la pantalla porque es la regla, y la misma línea la lee
 * el pie de la columna en desktop y la barra de mobile.
 */
export const faltaDelAmbiente = (
  ambienteId: AmbienteDeEscena,
  ambientePropio: string,
  fondo: FondoDeEscena | null,
): string | null => {
  if (ambienteId === AMBIENTE_PROPIO_ID && !ambientePropio.trim()) {
    return 'Contá cómo es el ambiente.';
  }
  if (ambienteId === AMBIENTE_FONDO_ID && !fondo) {
    return 'Subí o elegí una foto del lugar.';
  }
  return null;
};

/**
 * El rol de cada producto DENTRO de la foto.
 *
 * Es la decisión propia del modo. El Catálogo saca una imagen POR PRODUCTO y ahí no hay
 * nada que jerarquizar; acá los 2-4 entran juntos y alguien tiene que quedar adelante: sin
 * decirlo, la IA reparte el protagonismo sola y la foto sirve para cualquier cosa menos
 * para vender uno de los muebles.
 */
export type RolProducto = 'protagonista' | 'apoyo' | 'acento';

export interface Rol {
  id: RolProducto;
  nombre: string;
  /** Qué le pasa al producto en la foto — lo que se lee al elegir el rol. */
  descripcion: string;
}

export const ROLES_PRODUCTO: Rol[] = [
  {
    id: 'protagonista',
    nombre: 'Protagonista',
    descripcion: 'Al frente, enfocado y con el mejor ángulo. Uno solo por escena.',
  },
  {
    id: 'apoyo',
    nombre: 'Apoyo',
    descripcion: 'Acompaña en el mismo plano, entero pero sin robar la atención.',
  },
  {
    id: 'acento',
    nombre: 'Acento',
    descripcion: 'Al costado o en segundo plano. Puede entrar recortado por el encuadre.',
  },
];

export const rolPorId = (id: RolProducto): Rol =>
  ROLES_PRODUCTO.find((r) => r.id === id) ?? ROLES_PRODUCTO[0];

/**
 * Cuántos productos entran en la escena.
 *
 * El MÍNIMO es 2 porque con uno solo esto es el modo Automático: no hay ambiente que
 * armar ni jerarquía que decidir. El MÁXIMO es 4 porque a partir del quinto la foto deja
 * de ser un ambiente y pasa a ser un depósito — y porque la card de productos muestra
 * hasta cuatro miniaturas antes de resumir en "+N".
 */
export const MIN_PRODUCTOS = 2;
export const MAX_PRODUCTOS = 4;

/** Cuántas escenas se pueden pedir de una. Es el `max` del contador del pie. */
export const MAX_ESCENAS = 4;
export const ESCENAS_DEFAULT = 2;

/**
 * Créditos por escena en 1K: lo mismo que una imagen suelta. Salió en 2 ("la escena arma
 * varios productos") y Mati lo bajó a 1 el 2026-09-28 al verlo en la pantalla
 * (ADR-2026-09-28-ambiente-completo-cobra-como-una-imagen, en los dos repos). La autoridad
 * es el backend (`creditosEstimados = escenas × tier`); acá sólo se dibuja el botón.
 */
export const CREDITOS_POR_ESCENA = 1;

/**
 * Lo que dice el botón: escenas × tier de resolución (1K=1, 2K=1, 4K=2), la misma fórmula
 * que el gate del backend y que Variantes de acabado. Sin el tier, el botón decía 1 en 2K
 * y el backend cobraba 2.
 */
export const costoEscenas = (cantidad: number, calidad?: ResolucionImagen | null): number =>
  cantidad * CREDITOS_POR_ESCENA * creditosPorResolucion(calidad);

/**
 * Los roles de una selección de productos, respetando lo que ya había elegido el usuario.
 *
 * Tres reglas, en este orden:
 *   1. lo que ya tenía rol lo conserva (cambiar de producto no reescribe los otros);
 *   2. lo que entra nuevo arranca en "apoyo" —el rol neutro—, salvo que todavía no haya
 *      protagonista;
 *   3. SIEMPRE hay exactamente un protagonista: si no quedó ninguno (se quitó el que lo
 *      era) lo toma el primero de la lista, y si quedaron dos —imposible por la UI, pero
 *      el estado lo permite— el segundo baja a apoyo.
 *
 * La regla 3 es la que hace que el bloque de la columna no pueda quedar en un estado que
 * el payload no sabe describir.
 */
export const rolesDeSeleccion = (
  ids: string[],
  previos: Record<string, RolProducto> = {},
): Record<string, RolProducto> => {
  const roles: Record<string, RolProducto> = {};
  let hayProtagonista = false;
  for (const id of ids) {
    const rol = previos[id];
    if (rol === 'protagonista' && !hayProtagonista) {
      roles[id] = 'protagonista';
      hayProtagonista = true;
    } else if (rol === 'protagonista') {
      roles[id] = 'apoyo';
    } else {
      roles[id] = rol ?? 'apoyo';
    }
  }
  if (!hayProtagonista && ids.length > 0) roles[ids[0]] = 'protagonista';
  return roles;
};

/**
 * Pasar un producto a protagonista: el que lo era baja a "apoyo".
 *
 * El intercambio va acá y no en la pantalla porque es la regla —un solo protagonista—,
 * no una animación de la UI.
 */
export const conProtagonista = (
  roles: Record<string, RolProducto>,
  id: string,
): Record<string, RolProducto> => {
  const siguiente: Record<string, RolProducto> = {};
  for (const [k, v] of Object.entries(roles)) {
    siguiente[k] = v === 'protagonista' ? 'apoyo' : v;
  }
  siguiente[id] = 'protagonista';
  return siguiente;
};
